<template>
  <ui-overlay-loader
    class="identification-details"
    :loading="identificationReLoading || identificationUpdating"
  >
    <div class="grid">
      <div class="grid-item grid-item-main">
        <ui-card
          class="col-inner center-items"
          v-if="!complianceReviewIdentificationId"
        >
          <identification-timer />
        </ui-card>
        <ui-card
          class="col-inner center-items"
          v-if="shouldMainPannelBeVisible"
        >
          <identification-main-panel
            :complianceReviewIdentificationId="complianceReviewIdentificationId"
          />
        </ui-card>
        <ui-card class="col-inner">
          <identification-main-info />
        </ui-card>
        <ui-card class="col-inner" v-if="hasRegistries">
          <identification-checks-registry />
        </ui-card>
        <ui-card class="col-inner" v-if="hasAmlScreening">
          <identification-checks-aml-screening />
        </ui-card>
        <ui-card class="col-inner">
          <identification-checks-rules />
        </ui-card>
        <ui-card class="col-inner" v-if="hasFraudChecks">
          <identification-checks-fraud />
        </ui-card>
      </div>
      <div class="grid-item grid-item-document">
        <ui-card class="col-inner" v-if="complianceReviewIdentificationId">
          <submission-for-compliance-reviews />
        </ui-card>
        <ui-card class="col-inner">
          <identification-registration />
        </ui-card>
        <ui-card class="col-inner" v-if="!isPhotoIdentification">
          <identification-identity-data />
        </ui-card>
        <ui-card
          class="col-inner"
          v-if="doesIdentityDocumentExist && isPhotoIdentification"
        >
          <identification-document-additional-data />
        </ui-card>
        <ui-card
          class="col-inner"
          v-if="doesIdentityDocumentExist && isPhotoIdentification"
        >
          <identification-document
            :complianceReviewIdentificationId="complianceReviewIdentificationId"
          />
        </ui-card>
        <ui-card class="col-inner" v-if="doAditionalDocumentsExist">
          <identification-additional-documents
            :complianceReviewIdentificationId="complianceReviewIdentificationId"
          />
        </ui-card>
        <ui-card class="col-inner">
          <company-procedures />
        </ui-card>
      </div>
      <div class="grid-item grid-item-media" v-if="shouldMediaBeVisible">
        <ui-card no-padding class="col-inner">
          <identification-photos />
        </ui-card>
      </div>
    </div>
  </ui-overlay-loader>
</template>

<script>
import { mapGetters } from "vuex";
import { IDENTIFICATION_LOCAL_STATUS } from "@/constants";
import { getters } from "@/store";
import IdentificationTimer from "@/components/IdentificationTimer";
import IdentificationMainPanel from "@/components/IdentificationMainPanel";
import IdentificationPhotos from "@/components/IdentificationPhotos";
import IdentificationChecksFraud from "@/components/IdentificationChecksFraud";
import IdentificationChecksRules from "@/components/IdentificationChecksRules";
import IdentificationChecksRegistry from "@/components/IdentificationChecksRegistry";
import IdentificationChecksAmlScreening from "@/components/IdentificationChecksAmlScreening";
import IdentificationDocument from "@/components/IdentificationDocument";
import IdentificationIdentityData from "@/components/IdentificationIdentityData";
import IdentificationDocumentAdditionalData from "@/components/IdentificationDocumentAdditionalData";
import IdentificationAdditionalDocuments from "@/components/IdentificationAdditionalDocuments";
import IdentificationRegistration from "@/components/IdentificationRegistration";
import IdentificationMainInfo from "@/components/IdentificationMainInfo";
import SubmissionForComplianceReviews from "@/components/SubmissionForComplianceReviews";
import CompanyProcedures from "@/components/CompanyProcedures";
import { hasIdentityDocument, hasAdditionalDocuments } from "@/utils/media";

export default {
  props: {
    complianceReviewIdentificationId: { type: String, default: null }
  },
  computed: {
    ...mapGetters([
      getters.currentIdentification,
      getters.identificationUpdating,
      getters.isComplianceReviewItemResolved,
      getters.isPhotoIdentification,
      getters.isWorking
    ]),
    identificationReLoading() {
      const identification = this.currentIdentification;
      return (
        identification?.status === IDENTIFICATION_LOCAL_STATUS.LOADING &&
        identification?.loaded === true
      );
    },
    hasRegistries() {
      return this.currentIdentification?.data?.registries?.data?.length > 0;
    },
    hasAmlScreening() {
      return !!this.currentIdentification?.data?.registries?.data?.some(
        reg => reg.name === "AML_SCREENING"
      );
    },
    hasFraudChecks() {
      return !!this.currentIdentification?.data?.fraudChecks?.length;
    },
    doesIdentityDocumentExist() {
      return hasIdentityDocument(this.currentIdentification?.data?.media);
    },
    doAditionalDocumentsExist() {
      return hasAdditionalDocuments(this.currentIdentification?.data?.media);
    },
    shouldMediaBeVisible() {
      return this.doesIdentityDocumentExist || !this.isPhotoIdentification;
    },
    shouldMainPannelBeVisible() {
      if (this.isWorking) {
        return true;
      }

      return !this.isComplianceReviewItemResolved;
    }
  },
  components: {
    IdentificationMainInfo,
    IdentificationTimer,
    IdentificationMainPanel,
    IdentificationPhotos,
    IdentificationRegistration,
    IdentificationDocument,
    IdentificationIdentityData,
    IdentificationDocumentAdditionalData,
    IdentificationChecksFraud,
    IdentificationChecksRules,
    IdentificationChecksRegistry,
    IdentificationChecksAmlScreening,
    IdentificationAdditionalDocuments,
    CompanyProcedures,
    SubmissionForComplianceReviews
  }
};
</script>

<style scoped lang="scss">
.identification-details {
  max-width: 110rem;
  margin: 0 auto;
}
.grid {
  display: grid;
  grid-template-areas:
    "main"
    "dcmn"
    "media";
  grid-template-rows: auto;
  grid-template-columns: 100%;
  row-gap: 1rem;
  column-gap: 1rem;
  align-items: start;
  padding: 1rem;
  @media all and (min-width: 60em) {
    grid-template-areas:
      "main media"
      "dcmn media";
    grid-template-columns: 1fr auto;
  }
  @media all and (min-width: 80em) {
    grid-template-areas: "main dcmn media";
    grid-template-columns: 1fr 1fr auto;
  }
}
.grid-item {
  min-width: 0;
}
.grid-item-main {
  grid-area: main;
}
.grid-item-document {
  grid-area: dcmn;
}
.grid-item-media {
  position: sticky;
  top: 1rem;
  grid-area: media;
}
.col-main-panel {
  flex: 0 0 30rem;
}
.col-inner {
  height: 100%;
  & + & {
    margin-top: 1rem;
  }
}
.center-items {
  display: flex;
  align-items: center;
}
</style>
